import { Box, type SxProps, type Theme } from "@mui/material";
import { type FC, type MouseEvent, type ReactNode, useState } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";

type Props = {
	/** The whole text, shown in the tooltip while the line is cut short. */
	text: string;
	/** What the line shows (the text itself, or the text with a search match highlighted). */
	children: ReactNode;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * One line that ends in "…" when it runs out of room, with the whole text in a tooltip only while it's cut short: it's
 * measured as the pointer arrives, so a line that fits never gets a tooltip repeating it.
 */
export const TruncatedText: FC<Props> = ({ text, children, sx, dataTestId }) => {
	const [isTruncated, setIsTruncated] = useState(false);
	const measure = (event: MouseEvent<HTMLElement>) => setIsTruncated(event.currentTarget.scrollWidth > event.currentTarget.clientWidth);
	return (
		<InHouseTooltip title={isTruncated ? text : ""} placement="top">
			<Box
				component="span"
				data-testid={dataTestId}
				onMouseEnter={measure}
				sx={[{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, ...(Array.isArray(sx) ? sx : [sx])]}
			>
				{children}
			</Box>
		</InHouseTooltip>
	);
};
